/**
 * Common API Schemas
 *
 * Shared request/response patterns used across all API endpoints.
 * Re-exports from the main api/index.ts for convenience.
 */

import { z } from 'zod'

/**
 * Error Response Schema
 * Used for all HTTP error responses (4xx, 5xx)
 */
export const ErrorResponseSchema = z.object({
  message: z.string().describe('Human-readable error message'),
  code: z.string().optional().describe('Machine-readable error code'),
  details: z.array(z.unknown()).optional().describe('Additional error details or validation errors')
})

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>

/**
 * Pagination Metadata Schema
 * Used in all paginated responses
 */
export const PaginationMetaSchema = z.object({
  total: z.number().min(0).describe('Total number of items available'),
  limit: z.number().min(1).describe('Maximum number of items per page'),
  offset: z.number().min(0).describe('Number of items skipped'),
  message: z.string().optional().describe('Optional pagination message'),
  truncated: z
    .boolean()
    .optional()
    .describe('True when the result set was clipped by a cap (total exceeds the returned page size)')
})

export type PaginationMeta = z.infer<typeof PaginationMetaSchema>

/**
 * Pagination Parameters Schema
 * Used in request schemas for paginated endpoints
 */
export const PaginationParamsSchema = z.object({
  limit: z
    .number()
    .min(1)
    .max(1000)
    .default(1000)
    .optional()
    .describe('Maximum number of items to return (1-1000, default: 1000)'),
  offset: z.number().min(0).default(0).optional().describe('Number of items to skip for pagination (default: 0)')
})

export type PaginationParams = z.infer<typeof PaginationParamsSchema>

/**
 * Generic API Response Envelope
 * Standard structure for all API responses
 */
export interface ApiResponseEnvelope<T> {
  data: T | null
  message: string
  meta?: Record<string, unknown>
}

/**
 * Paginated API Response Envelope
 * Standard structure for paginated API responses
 */
export interface PaginatedResponseEnvelope<T> {
  data: T[]
  message: string
  meta: PaginationMeta
}
